/**
 * LeadsTable - Table component for displaying leads with sorting and actions
 * Optimized for quick scanning to find who to contact next
 */

import React, { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { RefreshIcon, SendIcon } from "@/components/icons";
import type { Lead, LeadStatus } from "../Lead.types";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { LeadTagChips } from "./LeadTagChips";
import { useLeadTags } from "../hooks/useLeads";

interface SelectionMode {
  isActive: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
}

interface LeadsTableProps {
  leads: Lead[];
  onViewLead: (leadId: string) => void;
  onStatusChange: (leadId: string, status: LeadStatus) => void;
  onAddNote: (leadId: string) => void;
  onDeleteLead: (leadId: string) => void;
  onEnrichLead?: (leadId: string) => void;
  onGenerateOutreach?: (leadId: string) => void;
  onSendEmail?: (leadId: string) => void;
  enrichingIds?: string[];
  isUpdating?: boolean;
  selectionMode?: SelectionMode;
}

export function LeadsTable({
  leads,
  onViewLead,
  onStatusChange,
  onAddNote,
  onDeleteLead,
  onEnrichLead,
  onGenerateOutreach,
  onSendEmail,
  enrichingIds = [],
  isUpdating = false,
  selectionMode,
}: LeadsTableProps) {
  const isSelectionActive = selectionMode?.isActive ?? false;
  const selectedIds = selectionMode?.selectedIds ?? [];
  const allVisibleSelected = leads.length > 0 && leads.every(lead => selectedIds.includes(lead.id));
  const parentRef = useRef<HTMLDivElement>(null);
  const { data: tagCatalog = [] } = useLeadTags();
  const columnCount = isSelectionActive ? 10 : 9;

  const virtualizer = useVirtualizer({
    count: leads.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 68,
    overscan: 10,
  });
  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return "-";
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    }
    return num.toLocaleString();
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getPagespeedColor = (score: number | undefined) => {
    if (score === undefined || score === null) return { color: "text-gray-300", bg: "bg-gray-50" };
    if (score >= 90) return { color: "text-green-700", bg: "bg-green-100" };
    if (score >= 50) return { color: "text-amber-700", bg: "bg-amber-100" };
    return { color: "text-red-700", bg: "bg-red-100" };
  };

  const getScoreDisplay = (score: number | undefined) => {
    if (score === undefined || score === null) {
      return { text: "-", color: "text-gray-300", bg: "bg-gray-50" };
    }
    if (score > 200) {
      return { text: score.toFixed(0), color: "text-green-700", bg: "bg-green-100" };
    }
    if (score > 100) {
      return { text: score.toFixed(0), color: "text-amber-700", bg: "bg-amber-100" };
    }
    if (score > 50) {
      return { text: score.toFixed(0), color: "text-blue-700", bg: "bg-blue-100" };
    }
    return { text: score.toFixed(0), color: "text-gray-600", bg: "bg-gray-100" };
  };

  const renderStars = (rating: number | undefined) => {
    if (rating === undefined || rating === null) return null;
    const fullStars = Math.floor(rating);

    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <svg
            key={i}
            className={`w-3 h-3 ${
              i < fullStars ? "text-yellow-400 fill-current" : "text-gray-200"
            }`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  if (leads.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 py-16">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="mt-2 text-sm">No leads found</p>
        </div>
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - virtualItems[virtualItems.length - 1].end
      : 0;

  return (
    <div ref={parentRef} className="flex-1 min-h-0 overflow-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            {isSelectionActive && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() => selectionMode?.onSelectAll()}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500 cursor-pointer"
                  title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                />
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
              Score
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Lead
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tags
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Instagram
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Google
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Speed
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Contact
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paddingTop > 0 && (
            <tr>
              <td colSpan={columnCount} style={{ height: paddingTop, padding: 0 }} />
            </tr>
          )}
          {virtualItems.map((virtualRow) => {
            const lead = leads[virtualRow.index];
            const scoreDisplay = getScoreDisplay(lead.score);
            const isSelected = selectedIds.includes(lead.id);

            return (
              <tr
                key={lead.id}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-blue-50" : ""}`}
                onClick={() => {
                  if (isSelectionActive) {
                    selectionMode?.onToggle(lead.id);
                  } else {
                    onViewLead(lead.id);
                  }
                }}
              >
              {/* Selection checkbox */}
              {isSelectionActive && (
                <td className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      selectionMode?.onToggle(lead.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500 cursor-pointer"
                  />
                </td>
              )}
              {/* Score - prominent display */}
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={`inline-flex items-center justify-center w-12 h-8 rounded-md text-sm font-semibold ${scoreDisplay.color} ${scoreDisplay.bg}`}
                >
                  {scoreDisplay.text}
                </span>
              </td>

              {/* Lead name with sector/city as secondary info */}
              <td className="px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {lead.name}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                    {lead.sector && <span>{lead.sector}</span>}
                    {lead.sector && lead.city && <span>-</span>}
                    {lead.city && <span>{lead.city}</span>}
                    {lead.website && (
                      <a
                        href={lead.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Web
                      </a>
                    )}
                    {lead.facebookUrl && (
                      <a
                        href={lead.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:text-blue-800"
                        onClick={(e) => e.stopPropagation()}
                        title="Facebook"
                      >
                        FB
                      </a>
                    )}
                    {lead.tiktokUrl && (
                      <a
                        href={lead.tiktokUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-800 hover:text-black"
                        onClick={(e) => e.stopPropagation()}
                        title="TikTok"
                      >
                        TT
                      </a>
                    )}
                    {lead.hasEcommerce && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-emerald-100 text-emerald-700"
                        title={lead.ecommercePlatform ? `E-commerce: ${lead.ecommercePlatform}` : "E-commerce detected"}
                      >
                        Shop
                      </span>
                    )}
                    {lead.hasBooking && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-violet-100 text-violet-700"
                        title="Booking platform detected"
                      >
                        Book
                      </span>
                    )}
                  </div>
                  {(lead.emailAddress || lead.phone) && (
                    <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                      {lead.emailAddress && (
                        <a
                          href={`mailto:${lead.emailAddress}`}
                          className={`hover:text-blue-600 truncate max-w-[160px] ${
                            lead.emailInvalid ? "line-through text-red-400" : ""
                          }`}
                          onClick={(e) => e.stopPropagation()}
                          title={
                            lead.emailInvalid
                              ? `Invalid: ${lead.emailInvalidReason || "hard bounce"}`
                              : lead.emailAddress
                          }
                        >
                          {lead.emailAddress}
                        </a>
                      )}
                      {lead.emailInvalid && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-red-100 text-red-700"
                          title={lead.emailInvalidReason || "Email hard-bounced"}
                        >
                          Bounced
                        </span>
                      )}
                      {lead.phone && (
                        <a
                          href={`tel:${lead.phone}`}
                          className="hover:text-blue-600"
                          onClick={(e) => e.stopPropagation()}
                          title={lead.phone}
                        >
                          {lead.phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </td>

              {/* Tags - credential chips (TD-041) */}
              <td className="px-4 py-3 align-middle max-w-[180px]">
                <LeadTagChips tags={lead.tags} catalog={tagCatalog} />
              </td>

              {/* Instagram - followers and posts */}
              <td className="px-4 py-3 whitespace-nowrap">
                {lead.instagramFollowers ? (
                  <div className="flex items-center gap-2">
                    {lead.instagramUrl ? (
                      <a
                        href={lead.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-600 hover:text-pink-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                      </a>
                    ) : (
                      <svg className="w-4 h-4 text-gray-300" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatNumber(lead.instagramFollowers)}
                      </div>
                      {lead.instagramPosts !== undefined && (
                        <div className="text-xs text-gray-500">
                          {formatNumber(lead.instagramPosts)} posts
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-300 text-sm">-</span>
                )}
              </td>

              {/* Google - rating and reviews */}
              <td className="px-4 py-3 whitespace-nowrap">
                {lead.googleRating ? (
                  <div>
                    <div className="flex items-center gap-1">
                      {renderStars(lead.googleRating)}
                      <span className="text-sm font-medium text-gray-700 ml-1">
                        {lead.googleRating.toFixed(1)}
                      </span>
                    </div>
                    {lead.googleReviews !== undefined && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatNumber(lead.googleReviews)} reviews
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-300 text-sm">-</span>
                )}
              </td>

              {/* PageSpeed */}
              <td className="px-4 py-3 whitespace-nowrap">
                {lead.pagespeedMobile != null ? (
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`text-sm font-semibold ${getPagespeedColor(lead.pagespeedMobile).color}`}
                        title={`Mobile: ${lead.pagespeedMobile}/100`}
                      >
                        {lead.pagespeedMobile}
                      </span>
                      <span className="text-gray-300 text-xs">/</span>
                      <span
                        className={`text-sm ${getPagespeedColor(lead.pagespeedDesktop).color}`}
                        title={`Desktop: ${lead.pagespeedDesktop ?? "-"}/100`}
                      >
                        {lead.pagespeedDesktop ?? "-"}
                      </span>
                    </div>
                    {lead.pagespeedLcp != null && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {lead.pagespeedLcp}s LCP
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-300 text-sm">-</span>
                )}
              </td>

              {/* Status dropdown */}
              <td
                className="px-4 py-3 whitespace-nowrap"
                onClick={(e) => e.stopPropagation()}
              >
                <LeadStatusBadge
                  status={lead.status}
                  onStatusChange={(status: LeadStatus) =>
                    onStatusChange(lead.id, status)
                  }
                  disabled={isUpdating}
                />
              </td>

              {/* Last Contact */}
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={`text-sm ${
                    !lead.lastContactedAt
                      ? "text-gray-400"
                      : "text-gray-600"
                  }`}
                >
                  {formatDate(lead.lastContactedAt)}
                </span>
                {lead.contactAttempts > 0 && (
                  <div className="text-xs text-gray-400">
                    {lead.contactAttempts} attempt{lead.contactAttempts > 1 ? "s" : ""}
                  </div>
                )}
              </td>

              {/* Actions */}
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-1">
                  {lead.googleUrl && (
                    <a
                      href={lead.googleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-sm"
                      title="View on Google Maps"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </a>
                  )}
                  {onEnrichLead && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEnrichLead(lead.id);
                      }}
                      disabled={enrichingIds.includes(lead.id)}
                      className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-sm disabled:opacity-50"
                      title="Enrich lead"
                    >
                      <RefreshIcon
                        className={`w-4 h-4 ${enrichingIds.includes(lead.id) ? "animate-spin" : ""}`}
                      />
                    </button>
                  )}
                  {onGenerateOutreach && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onGenerateOutreach(lead.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-sm"
                      title="Generate outreach email"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  )}
                  {onSendEmail && lead.emailAddress && !lead.emailInvalid && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSendEmail(lead.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-sm"
                      title="Send email"
                    >
                      <SendIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddNote(lead.id);
                    }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm"
                    title="Add note"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteLead(lead.id);
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                    title="Delete lead"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
          {paddingBottom > 0 && (
            <tr>
              <td colSpan={columnCount} style={{ height: paddingBottom, padding: 0 }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
